import React from "react";
import { useNavigate } from "react-router-dom";
import { HeartPulse, RefreshCw, AlertTriangle, CheckCircle } from "lucide-react";
import { useDashboardGrupal } from "../hooks/useWellness";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const NIVEL_CONFIG: Record<string,{bg:string,text:string,border:string,label:string}> = {
  alto:  { bg:"bg-red-50",    text:"text-red-700",   border:"border-red-400",   label:"Alto riesgo" },
  medio: { bg:"bg-amber-50",  text:"text-amber-700", border:"border-amber-400", label:"Riesgo moderado" },
  bajo:  { bg:"bg-green-50",  text:"text-green-700", border:"border-green-400", label:"Óptimo" },
};

export default function WellnessDashboardPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const academiaId = user?.academia_id || "";
  const { data, isLoading, refetch } = useDashboardGrupal(academiaId);

  const jugadores = data?.jugadores || [];
  const altos = jugadores.filter((j: any) => j.riesgo_nivel === "alto").length;
  const medios = jugadores.filter((j: any) => j.riesgo_nivel === "medio").length;
  const sinWellness = jugadores.filter((j: any) => !j.wellness_hoy).length;

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <HeartPulse size={20} className="text-rose-500"/>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard Wellness</h1>
            </div>
            <p className="text-gray-500 text-sm">{data?.fecha ? `Actualizado: ${data.fecha}` : "Monitoreo del plantel"}</p>
          </div>
          <button onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors">
            <RefreshCw size={14}/> Actualizar
          </button>
        </div>

        {/* Resumen */}
        {!isLoading && jugadores.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-red-700">{altos}</div>
              <div className="text-xs text-red-600 mt-1 font-medium">Alto riesgo</div>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-700">{medios}</div>
              <div className="text-xs text-amber-600 mt-1 font-medium">Riesgo moderado</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-gray-700">{sinWellness}</div>
              <div className="text-xs text-gray-500 mt-1 font-medium">Sin registro hoy</div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {[1,2,3,4,5].map(i=>(
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-gray-200"/>
                <div className="flex-1"><div className="h-4 bg-gray-200 rounded w-1/3 mb-2"/><div className="h-3 bg-gray-100 rounded w-1/2"/></div>
                <div className="w-12 h-8 bg-gray-200 rounded"/>
              </div>
            ))}
          </div>
        ) : jugadores.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
            <HeartPulse size={40} className="text-gray-300 mx-auto mb-3"/>
            <p className="text-gray-500 font-medium">Sin datos de wellness</p>
            <p className="text-gray-400 text-sm mt-1">Configura una academia para ver el dashboard grupal</p>
          </div>
        ) : (
          <div className="space-y-2">
            {jugadores.map((j: any) => {
              const cfg = NIVEL_CONFIG[j.riesgo_nivel] || NIVEL_CONFIG.bajo;
              const initials = j.nombre.split(" ").map((n:string)=>n[0]).join("").slice(0,2).toUpperCase();
              return (
                <div key={j.jugador_id}
                  onClick={() => nav(`/jugadores/${j.jugador_id}`)}
                  className={`group bg-white rounded-xl border-l-4 border border-gray-200 ${cfg.border} p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all`}
                >
                  <div className={`w-11 h-11 rounded-full ${cfg.bg} flex items-center justify-center font-bold text-sm flex-shrink-0 ${cfg.text}`}>
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{j.nombre}</p>
                    {j.causa && <p className="text-xs text-gray-500 truncate mt-0.5">{j.causa}</p>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <div className={`text-xl font-bold ${cfg.text}`}>{j.riesgo_score}</div>
                      <div className={`text-xs font-medium ${cfg.text} ${cfg.bg} px-2 py-0.5 rounded-full`}>{cfg.label}</div>
                    </div>
                    <div className="text-lg">{j.wellness_hoy
                      ? <CheckCircle size={18} className="text-green-500"/>
                      : <AlertTriangle size={18} className="text-amber-500"/>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

const NIVEL_COLOR: Record<string,string> = { alto:"#B71C1C", medio:"#E65100", bajo:"#1B5E20" };
const NIVEL_BG: Record<string,string>    = { alto:"#FFEBEE", medio:"#FBE9E7", bajo:"#E8F5E9" };

